// src/pages/PagesUtilisateurs/DashboardUsers/components/DashboardModuleNav.jsx

import React from "react";

/* -------------------------------------------------------------------------- */
/*                                  Themes                                    */
/* -------------------------------------------------------------------------- */

const MODULE_THEMES = {
  payments: {
    icon: `
      bg-amber-50 text-amber-600
      dark:bg-amber-500/10 dark:text-amber-300
    `,
    badge: `
      border border-amber-200
      bg-amber-100 text-amber-700
      dark:border-amber-500/20
      dark:bg-amber-500/15 dark:text-amber-300
    `,
    activeIcon: `
      bg-white/15 text-white
    `,
  },

  notifications: {
    icon: `
      bg-blue-50 text-[#013E8E]
      dark:bg-blue-500/10 dark:text-blue-300
    `,
    badge: `
      border border-blue-200
      bg-blue-100 text-[#013E8E]
      dark:border-blue-500/20
      dark:bg-blue-500/15 dark:text-blue-300
    `,
    activeIcon: `
      bg-white/15 text-white
    `,
  },

  messages: {
    icon: `
      bg-sky-50 text-sky-600
      dark:bg-sky-500/10 dark:text-sky-300
    `,
    badge: `
      border border-sky-200
      bg-sky-100 text-sky-700
      dark:border-sky-500/20
      dark:bg-sky-500/15 dark:text-sky-300
    `,
    activeIcon: `
      bg-white/15 text-white
    `,
  },

  objectifs: {
    icon: `
      bg-emerald-50 text-emerald-600
      dark:bg-emerald-500/10 dark:text-emerald-300
    `,
    badge: `
      border border-emerald-200
      bg-emerald-100 text-emerald-700
      dark:border-emerald-500/20
      dark:bg-emerald-500/15 dark:text-emerald-300
    `,
    activeIcon: `
      bg-white/15 text-white
    `,
  },

  events: {
    icon: `
      bg-teal-50 text-teal-600
      dark:bg-teal-500/10 dark:text-teal-300
    `,
    badge: `
      border border-teal-200
      bg-teal-100 text-teal-700
      dark:border-teal-500/20
      dark:bg-teal-500/15 dark:text-teal-300
    `,
    activeIcon: `
      bg-white/15 text-white
    `,
  },

  techniques: {
    icon: `
      bg-amber-50 text-amber-500
      dark:bg-amber-500/10 dark:text-amber-300
    `,
    badge: `
      border border-amber-200
      bg-amber-100 text-amber-700
      dark:border-amber-500/20
      dark:bg-amber-500/15 dark:text-amber-300
    `,
    activeIcon: `
      bg-white/15 text-white
    `,
  },

  contact: {
    icon: `
      bg-violet-50 text-violet-600
      dark:bg-violet-500/10 dark:text-violet-300
    `,
    badge: `
      border border-violet-200
      bg-violet-100 text-violet-700
      dark:border-violet-500/20
      dark:bg-violet-500/15 dark:text-violet-300
    `,
    activeIcon: `
      bg-white/15 text-white
    `,
  },

  default: {
    icon: `
      bg-slate-100 text-slate-500
      dark:bg-slate-800 dark:text-slate-400
    `,
    badge: `
      border border-slate-200
      bg-slate-100 text-slate-600
      dark:border-slate-700
      dark:bg-slate-800 dark:text-slate-300
    `,
    activeIcon: `
      bg-white/15 text-white
    `,
  },
};

const getTheme = (moduleId) =>
  MODULE_THEMES[moduleId] || MODULE_THEMES.default;

/* -------------------------------------------------------------------------- */
/*                               CounterBadge                                 */
/* -------------------------------------------------------------------------- */

function CounterBadge({
  value,
  theme,
  active = false,
}) {
  const count = Number(value) || 0;

  if (count <= 0) {
    return null;
  }

  return (
    <span
      className={`
        inline-flex
        h-7 min-w-7
        shrink-0
        items-center justify-center
        rounded-full
        px-2
        text-[11px] font-black
        leading-none
        shadow-sm
        ${
          active
            ? `
              border border-white/20
              bg-white/15
              text-white
            `
            : theme.badge
        }
      `}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                            MobileModuleButton                              */
/* -------------------------------------------------------------------------- */

function MobileModuleButton({
  module,
  active,
  onClick,
}) {
  const Icon = module.icon;
  const theme = getTheme(module.id);

  return (
    <button
      type="button"
      onClick={onClick}
      title={module.title}
      className={`
        relative
        flex min-w-0
        items-center
        gap-2.5

        rounded-2xl
        border
        px-3 py-3

        text-left
        transition

        ${
          active
            ? `
              border-[#013E8E]
              bg-[#013E8E]
              text-white
              shadow-sm
            `
            : `
              border-slate-200
              bg-white
              text-slate-600

              hover:border-slate-300
              hover:bg-slate-50

              dark:border-slate-800
              dark:bg-slate-900
              dark:text-slate-300

              dark:hover:bg-slate-800
            `
        }
      `}
    >
      {/* Icône */}

      <span
        className={`
          flex h-9 w-9
          shrink-0
          items-center justify-center
          rounded-xl

          text-sm

          ${
            active
              ? theme.activeIcon
              : theme.icon
          }
        `}
      >
        <Icon />
      </span>

      {/* Libellé */}

      <span className="min-w-0 flex-1">
        <span
          className={`
            block
            text-xs font-extrabold
            leading-4

            ${
              active
                ? "text-white"
                : "text-slate-700 dark:text-slate-200"
            }
          `}
        >
          {module.navTitle}
        </span>
      </span>

      {/* Compteur */}

      <CounterBadge
        value={module.count}
        theme={theme}
        active={active}
      />
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*                           DesktopModuleButton                              */
/* -------------------------------------------------------------------------- */

function DesktopModuleButton({
  module,
  active,
  onClick,
}) {
  const Icon = module.icon;
  const theme = getTheme(module.id);

  return (
    <button
      type="button"
      onClick={onClick}
      title={module.title}
      className={`
        group
        relative

        flex w-full min-w-0
        items-center
        justify-between
        gap-3

        rounded-xl
        px-3 py-2.5

        text-left
        transition

        ${
          active
            ? `
              bg-blue-50
              text-[#013E8E]

              dark:bg-blue-500/10
              dark:text-blue-300
            `
            : `
              text-slate-600

              hover:bg-slate-50
              hover:text-slate-950

              dark:text-slate-300
              dark:hover:bg-slate-800
              dark:hover:text-white
            `
        }
      `}
    >
      {/* Barre active */}

      {active ? (
        <span
          className="
            absolute
            inset-y-2
            left-0

            w-1
            rounded-full

            bg-[#013E8E]
            dark:bg-blue-400
          "
        />
      ) : null}

      <span className="flex min-w-0 items-center gap-3">
        {/* Icône */}

        <span
          className={`
            flex h-9 w-9
            shrink-0
            items-center justify-center
            rounded-xl

            text-sm

            transition

            ${
              active
                ? `
                  bg-white
                  text-[#013E8E]
                  shadow-sm

                  dark:bg-slate-900
                  dark:text-blue-300
                `
                : theme.icon
            }
          `}
        >
          <Icon />
        </span>

        {/* Nom */}

        <span className="min-w-0">
          <span
            className={`
              block truncate
              text-[15px] font-semibold

              ${
                active
                  ? "text-[#013E8E] dark:text-blue-300"
                  : "text-slate-700 dark:text-slate-200"
              }
            `}
          >
            {module.navTitle}
          </span>
        </span>
      </span>

      {/* Compteur */}

      <CounterBadge
        value={module.count}
        theme={theme}
      />
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*                            DashboardModuleNav                              */
/* -------------------------------------------------------------------------- */

export default function DashboardModuleNav({
  modules = [],
  activeId,
  onChange,
}) {
  return (
    <>
      {/* ------------------------------------------------------------------ */}
      {/* Mobile                                                             */}
      {/* ------------------------------------------------------------------ */}

      <nav
        className="
          w-full
          min-w-0
          max-w-full
          lg:hidden
        "
      >
        <div
          className="
            grid
            w-full
            min-w-0

            grid-cols-2
            gap-2

            sm:grid-cols-3
          "
        >
          {modules.map((module) => (
            <MobileModuleButton
              key={module.id}
              module={module}
              active={activeId === module.id}
              onClick={() => onChange(module.id)}
            />
          ))}
        </div>
      </nav>

      {/* ------------------------------------------------------------------ */}
      {/* Desktop                                                            */}
      {/* ------------------------------------------------------------------ */}

      <aside className="hidden min-w-0 lg:block">
        <div
          className="
            sticky
            top-[150px]

            w-full
            min-w-0

            overflow-hidden
            rounded-[1.5rem]

            border border-slate-200
            bg-white

            shadow-sm

            dark:border-slate-800
            dark:bg-slate-900
          "
        >
          {/* Header menu */}

          <div
            className="
              border-b border-slate-200
              px-4 py-4

              dark:border-slate-800
            "
          >
            <p
              className="
                text-[10px]
                font-black
                uppercase
                tracking-[0.16em]
                text-slate-400
              "
            >
              Mon espace
            </p>

            <p
              className="
                mt-1
                text-sm
                font-semibold
                text-slate-900

                dark:text-white
              "
            >
              Accès rapide
            </p>
          </div>

          {/* Navigation */}

          <nav className="space-y-1.5 p-2">
            {modules.map((module) => (
              <DesktopModuleButton
                key={module.id}
                module={module}
                active={activeId === module.id}
                onClick={() => onChange(module.id)}
              />
            ))}
          </nav>
        </div>
      </aside>
    </>
  );
}